import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import clsx from "clsx";

export interface ContextMenuItem {
  id: string;
  label: string;
  onClick: () => void;
  destructive?: boolean;
  disabled?: boolean;
}

export interface ContextMenuState {
  x: number;
  y: number;
  items: ContextMenuItem[];
}

interface ContextMenuProps {
  state: ContextMenuState | null;
  onClose: () => void;
  /** Über Dialoge/Editoren (z. B. Dokumenteneditor z-[280]–z-[300]). */
  zIndexClass?: string;
}

const VIEWPORT_PAD = 8;
/** Über dem zentralen Modal-Stack (z-[500]+), unter Toasts (z-[2000]). */
const DEFAULT_Z_INDEX_CLASS = "z-[900]";

export function ContextMenu({
  state,
  onClose,
  zIndexClass = DEFAULT_Z_INDEX_CLASS,
}: ContextMenuProps) {
  const menuRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState(() => ({
    x: state?.x ?? 0,
    y: state?.y ?? 0,
  }));

  useLayoutEffect(() => {
    if (!state) return;
    setPosition({ x: state.x, y: state.y });
    if (!menuRef.current) return;
    const rect = menuRef.current.getBoundingClientRect();
    let x = state.x;
    let y = state.y;
    if (x + rect.width > window.innerWidth - VIEWPORT_PAD) {
      x = Math.max(VIEWPORT_PAD, window.innerWidth - rect.width - VIEWPORT_PAD);
    }
    if (y + rect.height > window.innerHeight - VIEWPORT_PAD) {
      y = Math.max(VIEWPORT_PAD, window.innerHeight - rect.height - VIEWPORT_PAD);
    }
    setPosition({ x, y });
  }, [state]);

  useEffect(() => {
    if (!state) return;

    function onKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }

    function onPointerDown(e: PointerEvent) {
      const target = e.target as Node | null;
      if (menuRef.current?.contains(target)) return;
      onClose();
    }

    function onScroll() {
      onClose();
    }

    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("pointerdown", onPointerDown, true);
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onClose);

    return () => {
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("pointerdown", onPointerDown, true);
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onClose);
    };
  }, [state, onClose]);

  if (!state) return null;

  return createPortal(
    <div
      ref={menuRef}
      role="menu"
      className={clsx(
        "eh-context-menu fixed min-w-[11rem] origin-top-left rounded-ios border border-separator bg-card p-1 shadow-ios-md ring-1 ring-black/5 focus:outline-none",
        zIndexClass,
      )}
      style={{ left: position.x, top: position.y }}
      onContextMenu={(e) => e.preventDefault()}
    >
      {state.items.map((item) => (
        <button
          key={item.id}
          type="button"
          role="menuitem"
          disabled={item.disabled}
          className={clsx(
            "flex w-full items-center rounded-md px-3 py-2 text-left text-sm transition",
            item.disabled && "cursor-not-allowed opacity-50",
            item.destructive
              ? "text-status-red hover:bg-status-red/10"
              : "text-label hover:bg-fill-tertiary",
          )}
          onMouseDown={(e) => {
            if (e.button !== 0 || item.disabled) return;
            // Fokus behalten, damit navigator.clipboard.write() erlaubt ist.
            e.preventDefault();
            e.stopPropagation();
            item.onClick();
            onClose();
          }}
        >
          {item.label}
        </button>
      ))}
    </div>,
    document.body,
  );
}
